import React, { useEffect, useMemo, useState } from 'react';
import './LandingAdmin.css';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';

const randomId = (prefix = 'item') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const normalizeTextKey = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
const slugify = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
const parseSubcategoriesText = (text = '') => {
  const seen = new Set();
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((name) => {
      const key = normalizeTextKey(name);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((name) => ({ id: slugify(name) || randomId('subcat'), name }));
};
const groupCategoriesByName = (categories = []) => {
  const groups = new Map();
  categories.forEach((category) => {
    const key = normalizeTextKey(category?.name || '');
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(category);
  });
  return Array.from(groups.values()).filter((group) => group.length > 1);
};
const getTimestampMs = (timestamp) => {
  if (!timestamp) return Number.MAX_SAFE_INTEGER;
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const ms = date.getTime();
    return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER;
  } catch (error) {
    return Number.MAX_SAFE_INTEGER;
  }
};
const mergeCategorySubcategories = (categories = []) => {
  const uniqueById = new Map();
  categories.forEach((category) => {
    (category?.subcategories || []).forEach((sub) => {
      const name = (sub?.name || '').trim();
      if (!name) return;
      const id = (sub?.id || slugify(name) || randomId('subcat')).trim();
      if (!id || uniqueById.has(id)) return;
      uniqueById.set(id, { id, name });
    });
  });
  return Array.from(uniqueById.values());
};

const emptyCategoryForm = () => ({
  name: '',
  description: '',
  subcategoriesText: '',
});

const emptyTaxForm = () => ({
  name: '',
  description: '',
  percentage: '',
  active: true,
});

const emptyProductForm = () => ({
  name: '',
  sku: '',
  description: '',
  basePrice: '',
  categoryId: '',
  subcategoryId: '',
  visible: true,
  stock: '',
  gallery: [],
  variants: [],
  addons: [],
  taxIds: [],
});

const emptyAddonCategoryForm = () => ({
  name: '',
  description: '',
  items: [],
});

const createAddonItem = () => ({
  id: randomId('addon-item'),
  name: '',
  price: '',
});

const createVariantAttribute = () => ({
  id: randomId('variant-attr'),
  attribute: '',
  values: [createVariantValue()],
});

const createVariantValue = () => ({
  id: randomId('variant-value'),
  value: '',
  price: '',
});

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-PR', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);

const formatDate = (timestamp) => {
  if (!timestamp) return '--';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-PR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch (error) {
    return '--';
  }
};

const isVideoUrl = (url = '') => /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(url);

const uploadMediaFile = async (file, pathPrefix) => {
  const safePath = `${pathPrefix}/${randomId(file.name)}`;
  const storageRef = ref(storage, safePath);
  const metadata = { contentType: file.type || 'application/octet-stream' };

  const uploadSimple = async () => {
    await uploadBytes(storageRef, file, metadata);
    return getDownloadURL(storageRef);
  };

  const uploadResumable = async () =>
    new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      uploadTask.on(
        'state_changed',
        null,
        (error) => reject(error),
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        }
      );
    });

  const shouldUseResumable = file.size > 5 * 1024 * 1024 || file.type.startsWith('video/');
  try {
    return shouldUseResumable ? await uploadResumable() : await uploadSimple();
  } catch (error) {
    console.error('❌ Error subiendo media (intentando fallback):', error?.serverResponse || error);
    return shouldUseResumable ? await uploadSimple() : await uploadResumable();
  }
};

const ecommerceTabs = [
  {
    id: 'categories',
    name: 'Categorías',
    description: 'Organiza la jerarquía de productos y subcategorías visibles.',
  },
  {
    id: 'products',
    name: 'Productos',
    description: 'Crea catálogos con galerías, variantes y add-ons.',
  },
  {
    id: 'addons',
    name: 'Add-ons',
    description: 'Define accesorios por categoría para reutilizarlos en productos.',
  },
  {
    id: 'taxes',
    name: 'Impuestos',
    description: 'Administra tasas aplicables y su disponibilidad.',
  },
  {
    id: 'orders',
    name: 'Ventas online',
    description: 'Monitorea pedidos, pagos y PDFs generados en la tienda.',
  },
];


const EcommerceAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [taxesLoading, setTaxesLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm());
  const [taxForm, setTaxForm] = useState(emptyTaxForm());
  const [productForm, setProductForm] = useState(emptyProductForm());
  const [addonCategories, setAddonCategories] = useState([]);
  const [addonCategoriesLoading, setAddonCategoriesLoading] = useState(true);
  const [addonCategoryForm, setAddonCategoryForm] = useState(emptyAddonCategoryForm());
  const [editingAddonCategoryId, setEditingAddonCategoryId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingTaxId, setEditingTaxId] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');
  const [categorySaving, setCategorySaving] = useState(false);
  const [productSaving, setProductSaving] = useState(false);
  const [categoryConsolidating, setCategoryConsolidating] = useState(false);

  const categoriesRef = collection(db, 'ecommerce_categories');
  const taxesRef = collection(db, 'ecommerce_taxes');
  const productsRef = collection(db, 'ecommerce_products');
  const ordersRef = collection(db, 'ecommerce_orders');
  const addonCategoriesRef = collection(db, 'ecommerce_addon_categories');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const snapshot = await getDocs(categoriesRef);
        setCategories(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (error) {
        console.error('Error fetching categories', error);
        setStatusMessage('❌ No se pudieron cargar las categorías.');
      } finally {
        setCategoriesLoading(false);
      }
    };
    const fetchTaxes = async () => {
      try {
        setTaxesLoading(true);
        const snapshot = await getDocs(taxesRef);
        setTaxes(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (error) {
        console.error('Error fetching taxes', error);
        setStatusMessage('❌ No se pudieron cargar los impuestos.');
      } finally {
        setTaxesLoading(false);
      }
    };
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const snapshot = await getDocs(productsRef);
        setProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (error) {
        console.error('Error fetching products', error);
        setStatusMessage('❌ No se pudieron cargar los productos.');
      } finally {
        setProductsLoading(false);
      }
    };
    const fetchOrders = async () => {
      try {
        setOrdersLoading(true);
        const ordersQuery = query(ordersRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(ordersQuery);
        setOrders(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (error) {
        console.error('Error fetching ecommerce orders', error);
        setStatusMessage('❌ No se pudieron cargar las ventas online.');
      } finally {
        setOrdersLoading(false);
      }
    };

    const fetchAddonCategories = async () => {
      try {
        setAddonCategoriesLoading(true);
        const snapshot = await getDocs(addonCategoriesRef);
        setAddonCategories(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (error) {
        console.error('Error fetching addon categories', error);
        setStatusMessage('❌ No se pudieron cargar los add-ons.');
      } finally {
        setAddonCategoriesLoading(false);
      }
    };

    fetchCategories();
    fetchTaxes();
    fetchProducts();
    fetchOrders();
    fetchAddonCategories();
  }, []);

  const resetStatusAfter = () => {
    setTimeout(() => setStatusMessage(''), 4000);
  };

  const duplicateCategoryGroups = useMemo(() => groupCategoriesByName(categories), [categories]);
  const duplicateCategoryExtraCount = useMemo(
    () => duplicateCategoryGroups.reduce((total, group) => total + (group.length - 1), 0),
    [duplicateCategoryGroups]
  );

  const consolidateDuplicateCategories = async () => {
    if (categoryConsolidating) return;
    if (duplicateCategoryGroups.length === 0) {
      setStatusMessage('✅ No hay categorías duplicadas por consolidar.');
      return resetStatusAfter();
    }

    setCategoryConsolidating(true);
    try {
      for (const group of duplicateCategoryGroups) {
        const ordered = [...group].sort((a, b) => {
          const timeDiff = getTimestampMs(a?.createdAt) - getTimestampMs(b?.createdAt);
          if (timeDiff !== 0) return timeDiff;
          return (a?.id || '').localeCompare(b?.id || '');
        });
        const primary = ordered[0];
        const duplicates = ordered.slice(1);
        const mergedSubcategories = mergeCategorySubcategories(ordered);
        const mergedDescription =
          (primary?.description || '').trim() ||
          ordered.map((item) => (item?.description || '').trim()).find(Boolean) ||
          '';

        await updateDoc(doc(db, 'ecommerce_categories', primary.id), {
          name: (primary?.name || '').trim(),
          nameKey: normalizeTextKey(primary?.name || ''),
          description: mergedDescription,
          subcategories: mergedSubcategories,
          updatedAt: serverTimestamp(),
        });

        for (const duplicate of duplicates) {
          const productsByCategory = await getDocs(query(productsRef, where('categoryId', '==', duplicate.id)));
          for (const productDoc of productsByCategory.docs) {
            await updateDoc(productDoc.ref, {
              categoryId: primary.id,
              updatedAt: serverTimestamp(),
            });
          }
          await deleteDoc(doc(db, 'ecommerce_categories', duplicate.id));
        }
      }

      const [categoriesSnapshot, productsSnapshot] = await Promise.all([getDocs(categoriesRef), getDocs(productsRef)]);
      setCategories(categoriesSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setProducts(productsSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setStatusMessage('✅ Categorías duplicadas consolidadas y productos reasignados.');
    } catch (error) {
      console.error('Error consolidating duplicate categories', error);
      setStatusMessage('❌ No se pudieron consolidar las categorías duplicadas.');
    } finally {
      setCategoryConsolidating(false);
      resetStatusAfter();
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (categorySaving || categoryConsolidating) return;
    const categoryName = categoryForm.name.trim();
    const nameKey = normalizeTextKey(categoryName);
    const currentEditing = categories.find((category) => category.id === editingCategoryId);
    const isKeepingSameName = Boolean(editingCategoryId) && normalizeTextKey(currentEditing?.name || '') === nameKey;
    const payload = {
      name: categoryName,
      nameKey,
      description: categoryForm.description.trim(),
      subcategories: parseSubcategoriesText(categoryForm.subcategoriesText),
      updatedAt: serverTimestamp(),
    };
    if (!payload.name) {
      setStatusMessage('⚠️ La categoría debe tener nombre.');
      return resetStatusAfter();
    }
    const localDuplicate = categories.find(
      (category) => normalizeTextKey(category.name || '') === nameKey && category.id !== editingCategoryId
    );
    if (localDuplicate && !isKeepingSameName) {
      setStatusMessage(`⚠️ La categoría "${localDuplicate.name}" ya existe. Usa editar para evitar duplicados.`);
      return resetStatusAfter();
    }
    setCategorySaving(true);
    try {
      const remoteSnapshot = await getDocs(categoriesRef);
      const remoteDuplicate = remoteSnapshot.docs.find(
        (docSnap) =>
          docSnap.id !== editingCategoryId && normalizeTextKey(docSnap.data()?.name || '') === nameKey
      );
      if (remoteDuplicate && !isKeepingSameName) {
        setStatusMessage('⚠️ Ya existe una categoría con ese nombre.');
        return;
      }
      if (editingCategoryId) {
        await updateDoc(doc(db, 'ecommerce_categories', editingCategoryId), payload);
        setStatusMessage('✅ Categoría actualizada.');
      } else {
        await addDoc(categoriesRef, { ...payload, createdAt: serverTimestamp() });
        setStatusMessage('✅ Categoría creada.');
      }
      setCategoryForm(emptyCategoryForm());
      setEditingCategoryId(null);
      const snapshot = await getDocs(categoriesRef);
      setCategories(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error('Error saving category', error);
      setStatusMessage('❌ No se pudo guardar la categoría.');
    } finally {
      setCategorySaving(false);
      resetStatusAfter();
    }
  };

  const handleCategoryEdit = (category) => {
    setEditingCategoryId(category.id);
    setCategoryForm({
      name: category.name || '',
      description: category.description || '',
      subcategoriesText: (category.subcategories || []).map((sub) => sub.name).join('\n'),
    });
  };

  const handleCategoryDelete = async (categoryId) => {
    if (!window.confirm('¿Eliminar esta categoría y sus subcategorías?')) return;
    try {
      await deleteDoc(doc(db, 'ecommerce_categories', categoryId));
      setStatusMessage('✅ Categoría eliminada.');
      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
    } catch (error) {
      console.error('Error deleting category', error);
      setStatusMessage('❌ No se pudo eliminar la categoría.');
    } finally {
      resetStatusAfter();
    }
  };

  const handleTaxSubmit = async (e) => {
    e.preventDefault();
    const percentage = parseFloat(taxForm.percentage);
    if (!taxForm.name.trim()) {
      setStatusMessage('⚠️ El impuesto debe tener nombre.');
      return resetStatusAfter();
    }
    if (Number.isNaN(percentage)) {
      setStatusMessage('⚠️ Ingresa un porcentaje válido.');
      return resetStatusAfter();
    }
    const payload = {
      name: taxForm.name.trim(),
      description: taxForm.description.trim(),
      percentage,
      active: Boolean(taxForm.active),
      updatedAt: serverTimestamp(),
    };
    try {
      if (editingTaxId) {
        await updateDoc(doc(db, 'ecommerce_taxes', editingTaxId), payload);
        setStatusMessage('✅ Impuesto actualizado.');
      } else {
        await addDoc(taxesRef, { ...payload, createdAt: serverTimestamp() });
        setStatusMessage('✅ Impuesto creado.');
      }
      setTaxForm(emptyTaxForm());
      setEditingTaxId(null);
      const snapshot = await getDocs(taxesRef);
      setTaxes(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error('Error saving tax', error);
      setStatusMessage('❌ No se pudo guardar el impuesto.');
    } finally {
      resetStatusAfter();
    }
  };

  const handleTaxEdit = (tax) => {
    setEditingTaxId(tax.id);
    setTaxForm({
      name: tax.name || '',
      description: tax.description || '',
      percentage: tax.percentage?.toString() || '',
      active: tax.active !== false,
    });
  };

  const handleTaxDelete = async (taxId) => {
    if (!window.confirm('¿Eliminar este impuesto?')) return;
    try {
      await deleteDoc(doc(db, 'ecommerce_taxes', taxId));
      setStatusMessage('✅ Impuesto eliminado.');
      setTaxes((prev) => prev.filter((tax) => tax.id !== taxId));
    } catch (error) {
      console.error('Error deleting tax', error);
      setStatusMessage('❌ No se pudo eliminar el impuesto.');
    } finally {
      resetStatusAfter();
    }
  };

  const handleAddonCategoryItemChange = (index, field, value) => {
    setAddonCategoryForm((prev) => {
      const items = [...(prev.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addAddonCategoryItem = () => {
    setAddonCategoryForm((prev) => ({
      ...prev,
      items: [...(prev.items || []), createAddonItem()],
    }));
  };

  const removeAddonCategoryItem = (index) => {
    setAddonCategoryForm((prev) => ({
      ...prev,
      items: (prev.items || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleAddonCategorySubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: addonCategoryForm.name.trim(),
      description: addonCategoryForm.description.trim(),
      items: (addonCategoryForm.items || [])
        .map((item) => ({
          id: item.id || randomId('addon-item'),
          name: (item.name || '').trim(),
          price: parseFloat(item.price) || 0,
        }))
        .filter((item) => item.name),
      updatedAt: serverTimestamp(),
    };
    if (!payload.name) {
      setStatusMessage('⚠️ La categoría de add-ons debe tener nombre.');
      return resetStatusAfter();
    }
    try {
      if (editingAddonCategoryId) {
        await updateDoc(doc(db, 'ecommerce_addon_categories', editingAddonCategoryId), payload);
        setStatusMessage('✅ Add-ons actualizados.');
      } else {
        await addDoc(addonCategoriesRef, { ...payload, createdAt: serverTimestamp() });
        setStatusMessage('✅ Add-ons creados.');
      }
      setAddonCategoryForm(emptyAddonCategoryForm());
      setEditingAddonCategoryId(null);
      const snapshot = await getDocs(addonCategoriesRef);
      setAddonCategories(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error('Error saving addon categories', error);
      setStatusMessage('❌ No se pudieron guardar los add-ons.');
    } finally {
      resetStatusAfter();
    }
  };

  const handleAddonCategoryEdit = (category) => {
    setEditingAddonCategoryId(category.id);
    setAddonCategoryForm({
      name: category.name || '',
      description: category.description || '',
      items: (category.items || []).map((item) => ({
        id: item.id || randomId('addon-item'),
        name: item.name || '',
        price: item.price?.toString() || '',
      })),
    });
  };

  const handleAddonCategoryDelete = async (categoryId) => {
    if (!window.confirm('¿Eliminar esta categoría de add-ons?')) return;
    try {
      await deleteDoc(doc(db, 'ecommerce_addon_categories', categoryId));
      setStatusMessage('✅ Categoría de add-ons eliminada.');
      setAddonCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
    } catch (error) {
      console.error('Error deleting addon category', error);
      setStatusMessage('❌ No se pudo eliminar la categoría de add-ons.');
    } finally {
      resetStatusAfter();
    }
  };

  const handleAddonChange = (index, field, value) => {
    setProductForm((prev) => {
      const addons = [...(prev.addons || [])];
      addons[index] = { ...addons[index], [field]: value };
      return { ...prev, addons };
    });
  };

  const toggleAddonFromCatalog = (category, item) => {
    setProductForm((prev) => {
      const addons = [...(prev.addons || [])];
      const matchIndex = addons.findIndex(
        (addon) =>
          (addon.id && addon.id === item.id) ||
          (addon.name === item.name && addon.categoryId === category.id)
      );
      if (matchIndex >= 0) {
        addons.splice(matchIndex, 1);
      } else {
        addons.push({
          id: item.id || randomId('addon'),
          name: item.name || '',
          price: item.price?.toString?.() || item.price || '',
          categoryId: category.id,
          categoryName: category.name || '',
        });
      }
      return { ...prev, addons };
    });
  };

  const addVariantAttribute = () => {
    setProductForm((prev) => ({
      ...prev,
      variants: [...(prev.variants || []), createVariantAttribute()],
    }));
  };

  const removeVariantAttribute = (index) => {
    setProductForm((prev) => ({
      ...prev,
      variants: (prev.variants || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleVariantAttributeChange = (index, value) => {
    setProductForm((prev) => {
      const variants = [...(prev.variants || [])];
      variants[index] = { ...(variants[index] || {}), attribute: value };
      return { ...prev, variants };
    });
  };

  const addVariantValue = (attributeIndex) => {
    setProductForm((prev) => {
      const variants = [...(prev.variants || [])];
      const current = variants[attributeIndex] || createVariantAttribute();
      const values = [...(current.values || []), createVariantValue()];
      variants[attributeIndex] = { ...current, values };
      return { ...prev, variants };
    });
  };

  const handleVariantValueChange = (attributeIndex, valueIndex, field, value) => {
    setProductForm((prev) => {
      const variants = [...(prev.variants || [])];
      const current = variants[attributeIndex] || createVariantAttribute();
      const values = [...(current.values || [])];
      values[valueIndex] = { ...(values[valueIndex] || createVariantValue()), [field]: value };
      variants[attributeIndex] = { ...current, values };
      return { ...prev, variants };
    });
  };

  const removeVariantValue = (attributeIndex, valueIndex) => {
    setProductForm((prev) => {
      const variants = [...(prev.variants || [])];
      const current = variants[attributeIndex];
      if (!current) return prev;
      const values = (current.values || []).filter((_, idx) => idx !== valueIndex);
      variants[attributeIndex] = { ...current, values };
      return { ...prev, variants };
    });
  };

  const addAddon = () => {
    setProductForm((prev) => ({
      ...prev,
      addons: [...(prev.addons || []), { name: '', price: '' }],
    }));
  };

  const removeAddon = (index) => {
    setProductForm((prev) => ({
      ...prev,
      addons: (prev.addons || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const pathPrefix = `ecommerce/products/${editingProductId || 'new'}`;
        const url = await uploadMediaFile(file, pathPrefix);
        uploadedUrls.push(url);
      }
      setProductForm((prev) => ({
        ...prev,
        gallery: [...(prev.gallery || []), ...uploadedUrls],
      }));
      event.target.value = '';
      setStatusMessage('✅ Media subida correctamente.');
      resetStatusAfter();
    } catch (error) {
      console.error('Error uploading gallery image', error);
      setStatusMessage('❌ No se pudieron subir algunos archivos.');
      resetStatusAfter();
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setProductForm((prev) => ({
      ...prev,
      gallery: (prev.gallery || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (productSaving) return;
    if (!productForm.name.trim()) {
      setStatusMessage('⚠️ El producto necesita un nombre.');
      return resetStatusAfter();
    }
    const payload = {
      name: productForm.name.trim(),
      sku: productForm.sku.trim(),
      description: productForm.description.trim(),
      basePrice: parseFloat(productForm.basePrice) || 0,
      categoryId: productForm.categoryId || '',
      subcategoryId: productForm.subcategoryId || '',
      visible: Boolean(productForm.visible),
      stock: Number(productForm.stock) || 0,
      gallery: [...(productForm.gallery || [])],
      variants: (productForm.variants || [])
        .map((variant) => ({
          attribute: (variant.attribute || '').trim(),
          values: (variant.values || [])
            .map((value) => ({
              value: (value.value || '').trim(),
              priceAdjustment: parseFloat(value.price) || 0,
            }))
            .filter((entry) => entry.value),
        }))
        .filter((variant) => variant.attribute && variant.values.length),
      addons: (productForm.addons || []).map((addon) => ({
        id: addon.id || randomId('addon'),
        name: addon.name || '',
        price: parseFloat(addon.price) || 0,
        categoryId: addon.categoryId || '',
        categoryName: addon.categoryName || '',
      })),
      taxIds: productForm.taxIds || [],
      updatedAt: serverTimestamp(),
    };
    setProductSaving(true);
    try {
      if (editingProductId) {
        await updateDoc(doc(db, 'ecommerce_products', editingProductId), payload);
        setStatusMessage('✅ Producto actualizado.');
      } else {
        await addDoc(productsRef, { ...payload, createdAt: serverTimestamp() });
        setStatusMessage('✅ Producto creado.');
      }
      setEditingProductId(null);
      setProductForm(emptyProductForm());
      const snapshot = await getDocs(productsRef);
      setProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error('Error saving product', error);
      setStatusMessage('❌ No se pudo guardar el producto.');
    } finally {
      setProductSaving(false);
      resetStatusAfter();
    }
  };

  const normalizeVariants = (variants = []) => {
    if (!Array.isArray(variants) || variants.length === 0) return [];
    if (variants[0]?.values) {
      return variants.map((variant) => ({
        id: randomId('variant-attr'),
        attribute: variant.attribute || '',
        values: Array.isArray(variant.values)
          ? variant.values.map((value) => ({
              id: randomId('variant-value'),
              value: value.value || '',
              price: (value.priceAdjustment ?? value.price ?? 0).toString(),
            }))
          : [createVariantValue()],
      }));
    }
    return variants.reduce((acc, variant) => {
      const attr = variant.attribute || '';
      const value = variant.value || variant.name || '';
      if (!attr || !value) return acc;
      const existing = acc.find((item) => item.attribute === attr);
      const price = (variant.priceAdjustment ?? variant.price ?? 0).toString();
      if (existing) {
        existing.values.push({ id: randomId('variant-value'), value, price });
      } else {
        acc.push({
          id: randomId('variant-attr'),
          attribute: attr,
          values: [{ id: randomId('variant-value'), value, price }],
        });
      }
      return acc;
    }, []);
  };

  const handleProductEdit = (product) => {
    setEditingProductId(product.id);
    setProductForm({
      name: product.name || '',
      sku: product.sku || '',
      description: product.description || '',
      basePrice: product.basePrice?.toString() || '',
      categoryId: product.categoryId || '',
      subcategoryId: product.subcategoryId || '',
      visible: product.visible !== false,
      stock: product.stock?.toString() || '',
      gallery: Array.isArray(product.gallery) ? [...product.gallery] : [],
      variants: normalizeVariants(product.variants || []),
      addons: (product.addons || []).map((addon) => ({
        ...addon,
        price: addon.price?.toString() || '',
      })),
      taxIds: product.taxIds || [],
    });
  };

  const handleProductDelete = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await deleteDoc(doc(db, 'ecommerce_products', productId));
      setStatusMessage('✅ Producto eliminado.');
      setProducts((prev) => prev.filter((product) => product.id !== productId));
    } catch (error) {
      console.error('Error deleting product', error);
      setStatusMessage('❌ No se pudo eliminar el producto.');
    } finally {
      resetStatusAfter();
    }
  };

  const subcategoriesForSelectedCategory = useMemo(() => {
    const selected = categories.find((cat) => cat.id === productForm.categoryId);
    return selected?.subcategories || [];
  }, [categories, productForm.categoryId]);

  const toggleProductVisibility = async (product) => {
    try {
      await updateDoc(doc(db, 'ecommerce_products', product.id), {
        visible: !product.visible,
        updatedAt: serverTimestamp(),
      });
      setProducts((prev) =>
        prev.map((item) => (item.id === product.id ? { ...item, visible: !product.visible } : item))
      );
    } catch (error) {
      console.error('Error toggling visibility', error);
      setStatusMessage('❌ No se pudo actualizar la visibilidad.');
      resetStatusAfter();
    }
  };

  const activeTabConfig = ecommerceTabs.find((tab) => tab.id === activeTab);

  return (
    <div className="min-h-screen bg-[var(--admin-surface)]">
      <main className="px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl space-y-10">
          <div className="rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <h1 className="text-3xl font-bold text-[var(--admin-text)]">Gestión de eCommerce</h1>
            <p className="text-[var(--admin-muted)] mt-2">
              Administra categorías, subcategorías, productos, impuestos, variantes y las ventas online registradas.
            </p>
            {statusMessage && (
              <div className="mt-4 rounded-2xl border border-[var(--admin-border)] bg-white px-4 py-3 text-sm text-[var(--admin-text)] shadow-sm">
                {statusMessage}
              </div>
            )}
            <div className="mt-6">
              <div className="overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                <nav className="flex min-w-max flex-nowrap gap-2 rounded-2xl bg-[var(--admin-surface)] p-2">
                  {ecommerceTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={[
                        'flex flex-none min-w-[200px] flex-col rounded-2xl px-4 py-3 text-left transition',
                        activeTab === tab.id
                          ? 'bg-[var(--admin-primary)] text-white shadow-lg shadow-emerald-100'
                          : 'text-[var(--admin-muted)] hover:bg-white hover:text-[var(--admin-primary)]',
                      ].join(' ')}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wide">{tab.name}</span>
                      <span className="text-[11px] leading-tight">{tab.description}</span>
                    </button>
                  ))}
                </nav>
              </div>
              {activeTabConfig && (
                <p className="mt-3 text-xs uppercase tracking-widest text-[var(--admin-muted)]">
                  Vista activa: {activeTabConfig.name}
                </p>
              )}
            </div>
          </div>

          {activeTab === 'categories' && (
          <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <header className="flex flex-col gap-2 pb-4 border-b border-[var(--admin-border)] mb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  Categorías y subcategorías
                </p>
                <h2 className="text-xl font-semibold text-[var(--admin-text)]">Organiza la tienda</h2>
              </div>
              {categoriesLoading && <span className="text-xs text-[var(--admin-muted)]">Cargando categorías...</span>}
            </header>
            {duplicateCategoryGroups.length > 0 && (
              <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p>
                    Se detectaron {duplicateCategoryExtraCount} categorías duplicadas. Puedes consolidarlas sin perder
                    productos.
                  </p>
                  <button
                    type="button"
                    onClick={consolidateDuplicateCategories}
                    disabled={categoryConsolidating || categorySaving}
                    className={[
                      'rounded-full px-4 py-1 text-xs font-semibold',
                      categoryConsolidating || categorySaving
                        ? 'cursor-not-allowed bg-amber-200 text-amber-700'
                        : 'bg-amber-600 text-white hover:bg-amber-700',
                    ].join(' ')}
                  >
                    {categoryConsolidating ? 'Consolidando...' : 'Consolidar duplicados'}
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleCategorySubmit} className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-semibold text-[var(--admin-muted)]">
                Nombre
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-[var(--admin-muted)]">
                Descripción
                <input
                  type="text"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-[var(--admin-muted)] md:col-span-2">
                Subcategorías (una por línea)
                <textarea
                  value={categoryForm.subcategoriesText}
                  onChange={(e) => setCategoryForm((prev) => ({ ...prev, subcategoriesText: e.target.value }))}
                  className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  rows={3}
                />
              </label>
              <div className="md:col-span-2 flex gap-3">
                <button
                  type="submit"
                  disabled={categorySaving || categoryConsolidating}
                  className={[
                    'rounded-2xl bg-[var(--admin-primary)] px-5 py-2 text-sm font-semibold text-white shadow',
                    categorySaving || categoryConsolidating
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-[var(--admin-primary-dark)]',
                  ].join(' ')}
                >
                  {categorySaving ? 'Guardando...' : editingCategoryId ? 'Actualizar categoría' : 'Crear categoría'}
                </button>
                {editingCategoryId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCategoryId(null);
                      setCategoryForm(emptyCategoryForm());
                    }}
                    className="rounded-2xl border border-[var(--admin-border)] px-4 py-2 text-sm font-semibold text-[var(--admin-muted)] hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
            <div className="mt-6 grid gap-4">
              {categories.map((category) => (
                <article
                  key={category.id}
                  className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[var(--admin-text)]">{category.name}</h3>
                      <p className="text-sm text-[var(--admin-muted)]">{category.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleCategoryEdit(category)}
                        className="rounded-full border border-[var(--admin-border)] px-4 py-1 text-sm font-medium text-[var(--admin-text)]"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCategoryDelete(category.id)}
                        className="rounded-full bg-red-50 px-4 py-1 text-sm font-medium text-[var(--admin-danger)]"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  {category.subcategories?.length > 0 && (
                    <div className="mt-3 text-sm text-[var(--admin-muted)]">
                      <span className="font-semibold text-[var(--admin-text)]">Subcategorías:</span>{' '}
                      {category.subcategories.map((sub) => sub.name).join(', ')}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
          )}

          {activeTab === 'orders' && (
          <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <header className="flex flex-col gap-2 pb-4 border-b border-[var(--admin-border)] mb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  Ventas online registradas
                </p>
                <h2 className="text-xl font-semibold text-[var(--admin-text)]">
                  Pedidos confirmados vía checkout
                </h2>
              </div>
              {ordersLoading && <span className="text-xs text-[var(--admin-muted)]">Cargando ventas...</span>}
            </header>
            {orders.length === 0 && !ordersLoading ? (
              <p className="text-sm text-[var(--admin-muted)]">Aún no hay ventas registradas.</p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-[var(--admin-border)]">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-[var(--admin-surface)] text-[var(--admin-muted)]">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Orden</th>
                      <th className="px-4 py-3 font-semibold">Cliente</th>
                      <th className="px-4 py-3 font-semibold">Fecha</th>
                      <th className="px-4 py-3 font-semibold">Total</th>
                      <th className="px-4 py-3 font-semibold">Estado</th>
                      <th className="px-4 py-3 font-semibold">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className="border-t border-[var(--admin-border)] text-[var(--admin-text)]">
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-[var(--admin-text)]">{order.orderId || order.id}</span>
                            <span className="text-xs text-[var(--admin-muted)]">{order.checkoutReference || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-semibold">{order.customer?.fullName || 'Sin nombre'}</span>
                            <span className="text-xs text-[var(--admin-muted)]">{order.customer?.email || ''}</span>
                            <span className="text-xs text-[var(--admin-muted)]">{order.customer?.phone || ''}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[var(--admin-muted)]">{formatDate(order.createdAt)}</td>
                        <td className="px-4 py-3 font-semibold">{formatCurrency(order.totals?.total || 0)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={[
                              'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
                              order.status === 'paid'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-700',
                            ].join(' ')}
                          >
                            {order.status || 'pendiente'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {order.pdfUrl ? (
                            <a
                              href={order.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-[var(--admin-primary)] underline"
                            >
                              Descargar
                            </a>
                          ) : (
                            <span className="text-xs text-[var(--admin-muted)]">No disponible</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          )}

          {activeTab === 'taxes' && (
          <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <header className="flex flex-col gap-2 pb-4 border-b border-[var(--admin-border)] mb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  Impuestos y tasas
                </p>
                <h2 className="text-xl font-semibold text-[var(--admin-text)]">Configura impuestos aplicables</h2>
              </div>
              {taxesLoading && <span className="text-xs text-[var(--admin-muted)]">Cargando impuestos...</span>}
            </header>
            <form onSubmit={handleTaxSubmit} className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-semibold text-[var(--admin-muted)]">
                Nombre
                <input
                  type="text"
                  value={taxForm.name}
                  onChange={(e) => setTaxForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-[var(--admin-muted)]">
                Porcentaje (%)
                <input
                  type="number"
                  value={taxForm.percentage}
                  onChange={(e) => setTaxForm((prev) => ({ ...prev, percentage: e.target.value }))}
                  className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  step="0.01"
                />
              </label>
              <label className="text-sm font-semibold text-[var(--admin-muted)] md:col-span-2">
                Descripción
                <input
                  type="text"
                  value={taxForm.description}
                  onChange={(e) => setTaxForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                />
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-[var(--admin-text)]">
                <input
                  type="checkbox"
                  checked={taxForm.active}
                  onChange={(e) => setTaxForm((prev) => ({ ...prev, active: e.target.checked }))}
                  className="rounded border-[var(--admin-border)] text-[var(--admin-primary)]"
                />
                Activo
              </label>
              <div className="md:col-span-2 flex gap-3">
                <button
                  type="submit"
                  className="rounded-2xl bg-[var(--admin-primary)] px-5 py-2 text-sm font-semibold text-white shadow hover:bg-[var(--admin-primary-dark)]"
                >
                  {editingTaxId ? 'Actualizar impuesto' : 'Crear impuesto'}
                </button>
                {editingTaxId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTaxId(null);
                      setTaxForm(emptyTaxForm());
                    }}
                    className="rounded-2xl border border-[var(--admin-border)] px-4 py-2 text-sm font-semibold text-[var(--admin-muted)] hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
            <div className="mt-6 grid gap-4">
              {taxes.map((tax) => (
                <article
                  key={tax.id}
                  className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[var(--admin-text)]">{tax.name}</h3>
                      <p className="text-sm text-[var(--admin-muted)]">{tax.description}</p>
                      <p className="text-sm text-[var(--admin-text)]">
                        {tax.percentage}% · {tax.active === false ? 'Inactivo' : 'Activo'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleTaxEdit(tax)}
                        className="rounded-full border border-[var(--admin-border)] px-4 py-1 text-sm font-medium text-[var(--admin-text)]"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTaxDelete(tax.id)}
                        className="rounded-full bg-red-50 px-4 py-1 text-sm font-medium text-[var(--admin-danger)]"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
          )}

          {activeTab === 'products' && (
          <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <header className="flex flex-col gap-2 pb-4 border-b border-[var(--admin-border)] mb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  Productos, variantes y addons
                </p>
                <h2 className="text-xl font-semibold text-[var(--admin-text)]">
                  Publica el catálogo para el cliente
                </h2>
              </div>
              {productsLoading && <span className="text-xs text-[var(--admin-muted)]">Cargando productos...</span>}
            </header>
            <form onSubmit={handleProductSubmit} className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Nombre del producto
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  SKU
                  <input
                    type="text"
                    value={productForm.sku}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, sku: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <label className="text-sm font-semibold text-[var(--admin-muted)]">
                Descripción
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="mt-1 rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  rows={3}
                />
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Precio base
                  <input
                    type="number"
                    value={productForm.basePrice}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, basePrice: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                    step="0.01"
                  />
                </label>
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Inventario disponible
                  <input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, stock: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Categoría
                  <select
                    value={productForm.categoryId}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        categoryId: e.target.value,
                        subcategoryId: '',
                      }))
                    }
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  >
                    <option value="">Selecciona</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Subcategoría
                  <select
                    value={productForm.subcategoryId}
                    onChange={(e) => setProductForm((prev) => ({ ...prev, subcategoryId: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  >
                    <option value="">Selecciona</option>
                    {subcategoriesForSelectedCategory.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Impuestos aplicables
                  <select
                    multiple
                    value={productForm.taxIds}
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        taxIds: Array.from(e.target.selectedOptions).map((opt) => opt.value),
                      }))
                    }
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  >
                    {taxes.map((tax) => (
                      <option key={tax.id} value={tax.id}>
                        {tax.name} ({tax.percentage}%)
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--admin-text)]">Galería de producto</p>
                    <p className="text-xs text-[var(--admin-muted)]">
                      Sube fotos, videos o GIFs en alta resolución.
                    </p>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[var(--admin-border)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--admin-text)] hover:bg-white">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      onChange={handleGalleryUpload}
                    />
                    {galleryUploading ? 'Subiendo...' : 'Subir media'}
                  </label>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {(productForm.gallery || []).map((img, index) => (
                    <div
                      key={`${img}-${index}`}
                      className="relative h-24 w-24 overflow-hidden rounded-xl border border-[var(--admin-border)] bg-white"
                    >
                      {isVideoUrl(img) ? (
                        <video src={img} className="h-full w-full object-cover" muted playsInline />
                      ) : (
                        <img src={img} alt={`gallery-${index}`} className="h-full w-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(index)}
                        className="absolute right-1 top-1 rounded-full bg-white/80 px-2 text-xs font-semibold text-[var(--admin-danger)]"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(productForm.gallery || []).length === 0 && (
                    <p className="text-sm text-[var(--admin-muted)]">Aún no has subido imágenes.</p>
                  )}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[var(--admin-text)]">
                <input
                  type="checkbox"
                  checked={productForm.visible}
                  onChange={(e) => setProductForm((prev) => ({ ...prev, visible: e.target.checked }))}
                  className="rounded border-[var(--admin-border)] text-[var(--admin-primary)]"
                />
                Mostrar en la tienda
              </label>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[var(--admin-text)]">Variantes por atributo</p>
                      <p className="text-xs text-[var(--admin-muted)]">
                        Define un atributo (Color, Cristal, etc.) y sus opciones con precio opcional.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addVariantAttribute}
                      className="text-sm font-semibold text-[var(--admin-primary)]"
                    >
                      + Atributo
                    </button>
                  </div>
                  <div className="mt-3 space-y-4">
                    {(productForm.variants || []).map((variant, index) => (
                      <div key={variant.id || index} className="rounded-2xl border border-[var(--admin-border)] bg-white p-3 space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <input
                            type="text"
                            value={variant.attribute}
                            onChange={(e) => handleVariantAttributeChange(index, e.target.value)}
                            className="flex-1 rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                            placeholder="Nombre del atributo (ej. Color)"
                          />
                          <button
                            type="button"
                            onClick={() => removeVariantAttribute(index)}
                            className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-danger)]"
                          >
                            Eliminar atributo
                          </button>
                        </div>
                        <div className="space-y-3">
                          {(variant.values || []).map((value, valueIndex) => (
                            <div
                              key={value.id || valueIndex}
                              className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-3"
                            >
                              <div className="grid gap-3 md:grid-cols-[2fr,1fr,auto]">
                                <input
                                  type="text"
                                  value={value.value}
                                  onChange={(e) =>
                                    handleVariantValueChange(index, valueIndex, 'value', e.target.value)
                                  }
                                  className="rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                                  placeholder="Valor (ej. Blanco)"
                                />
                                <input
                                  type="number"
                                  value={value.price}
                                  onChange={(e) =>
                                    handleVariantValueChange(index, valueIndex, 'price', e.target.value)
                                  }
                                  className="rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                                  placeholder="Ajuste de precio"
                                  step="0.01"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeVariantValue(index, valueIndex)}
                                  className="text-sm font-semibold text-[var(--admin-danger)]"
                                >
                                  Quitar
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addVariantValue(index)}
                            className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-primary)]"
                          >
                            + Añadir opción
                          </button>
                        </div>
                      </div>
                    ))}
                    {(productForm.variants || []).length === 0 && (
                      <p className="text-sm text-[var(--admin-muted)]">No hay atributos configurados.</p>
                    )}
                  </div>
                </div>
                <div className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[var(--admin-text)]">Accesorios / Add-ons</p>
                    <button
                      type="button"
                      onClick={addAddon}
                      className="text-sm font-semibold text-[var(--admin-primary)]"
                    >
                      + Add-on manual
                    </button>
                  </div>
                  <div className="mt-3 space-y-4">
                    {addonCategoriesLoading ? (
                      <p className="text-sm text-[var(--admin-muted)]">Cargando add-ons...</p>
                    ) : addonCategories.length === 0 ? (
                      <p className="text-sm text-[var(--admin-muted)]">
                        No hay categorías de add-ons creadas. Crea una en la pestaña “Add-ons”.
                      </p>
                    ) : (
                      addonCategories.map((category) => (
                        <div
                          key={category.id}
                          className="rounded-2xl border border-[var(--admin-border)] bg-white p-3 space-y-2"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-[var(--admin-text)]">{category.name}</p>
                            {category.description && (
                              <span className="text-xs text-[var(--admin-muted)]">{category.description}</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(category.items || []).map((item) => {
                              const isSelected = (productForm.addons || []).some(
                                (addon) =>
                                  (addon.id && addon.id === item.id) ||
                                  (addon.name === item.name && addon.categoryId === category.id)
                              );
                              return (
                                <button
                                  key={item.id || item.name}
                                  type="button"
                                  onClick={() => toggleAddonFromCatalog(category, item)}
                                  className={[
                                    'rounded-full border px-3 py-1 text-xs font-semibold transition',
                                    isSelected
                                      ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                                      : 'border-[var(--admin-border)] text-[var(--admin-muted)]',
                                  ].join(' ')}
                                >
                                  {item.name}
                                  {Number.isFinite(item.price) ? ` (${formatCurrency(item.price)})` : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                        Add-ons seleccionados
                      </p>
                      <div className="mt-2 space-y-3">
                        {(productForm.addons || []).map((addon, index) => (
                          <div
                            key={`addon-${index}`}
                            className="rounded-2xl border border-[var(--admin-border)] bg-white p-3 space-y-2"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <input
                                type="text"
                                value={addon.name}
                                onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                                className="w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                                placeholder="Nombre"
                              />
                              {addon.categoryName && (
                                <span className="text-xs text-[var(--admin-muted)]">{addon.categoryName}</span>
                              )}
                            </div>
                            <input
                              type="number"
                              value={addon.price}
                              onChange={(e) => handleAddonChange(index, 'price', e.target.value)}
                              className="w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                              placeholder="Precio"
                              step="0.01"
                            />
                            <button
                              type="button"
                              onClick={() => removeAddon(index)}
                              className="text-sm text-[var(--admin-danger)]"
                            >
                              Eliminar
                            </button>
                          </div>
                        ))}
                        {(productForm.addons || []).length === 0 && (
                          <p className="text-sm text-[var(--admin-muted)]">No hay accesorios agregados.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={productSaving}
                  className={[
                    'rounded-2xl bg-[var(--admin-primary)] px-6 py-2 text-sm font-semibold text-white shadow',
                    productSaving ? 'cursor-not-allowed opacity-60' : 'hover:bg-[var(--admin-primary-dark)]',
                  ].join(' ')}
                >
                  {productSaving ? 'Guardando...' : editingProductId ? 'Actualizar producto' : 'Crear producto'}
                </button>
                {editingProductId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingProductId(null);
                      setProductForm(emptyProductForm());
                    }}
                    className="rounded-2xl border border-[var(--admin-border)] px-4 py-2 text-sm font-semibold text-[var(--admin-muted)] hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>

            <div className="mt-8 grid gap-4">
              {products.map((product) => (
                <article key={product.id} className="rounded-3xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[var(--admin-text)]">{product.name}</h3>
                      <p className="text-sm text-[var(--admin-muted)]">{product.description}</p>
                      <p className="text-sm text-[var(--admin-text)]">
                        ${product.basePrice?.toFixed?.(2) || Number(product.basePrice || 0).toFixed(2)} · Stock:{' '}
                        {product.stock ?? 0}
                      </p>
                      <p className="text-xs text-[var(--admin-muted)]">
                        {product.visible ? 'Visible al cliente' : 'Oculto'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => toggleProductVisibility(product)}
                        className="rounded-full border border-[var(--admin-border)] px-4 py-1 text-sm font-medium text-[var(--admin-text)]"
                      >
                        {product.visible ? 'Ocultar' : 'Mostrar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleProductEdit(product)}
                        className="rounded-full border border-[var(--admin-border)] px-4 py-1 text-sm font-medium text-[var(--admin-text)]"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleProductDelete(product.id)}
                        className="rounded-full bg-red-50 px-4 py-1 text-sm font-medium text-[var(--admin-danger)]"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  {Array.isArray(product.variants) && product.variants.length > 0 && (
                    <div className="mt-3 space-y-2 text-sm text-[var(--admin-muted)]">
                      <span className="block font-semibold text-[var(--admin-text)]">Variantes:</span>
                      {product.variants.map((variant, idx) => (
                        <div key={`${product.id}-variant-${idx}`}>
                          <p className="font-semibold text-[var(--admin-text)]">{variant.attribute}</p>
                          <ul className="ml-3 list-disc space-y-1">
                            {(variant.values || []).map((value, valueIdx) => (
                              <li key={`${product.id}-variant-${idx}-${valueIdx}`}>
                                {value.value}
                                {value.priceAdjustment
                                  ? ` · Ajuste: ${Number(value.priceAdjustment).toFixed(2)}`
                                  : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  {product.addons?.length > 0 && (
                    <div className="mt-2 text-sm text-[var(--admin-muted)]">
                      <span className="font-semibold text-[var(--admin-text)]">Add-ons:</span>{' '}
                      {product.addons.map((addon) => addon.name).join(', ')}
                    </div>
                  )}
                  {product.gallery?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {product.gallery.slice(0, 4).map((img, idx) => (
                        isVideoUrl(img) ? (
                          <video
                            key={`${product.id}-gallery-${idx}`}
                            src={img}
                            className="h-16 w-16 rounded-lg border border-[var(--admin-border)] object-cover"
                            muted
                            playsInline
                          />
                        ) : (
                          <img
                            key={`${product.id}-gallery-${idx}`}
                            src={img}
                            alt={`${product.name}-${idx}`}
                            className="h-16 w-16 rounded-lg border border-[var(--admin-border)] object-cover"
                          />
                        )
                      ))}
                    </div>
                  )}
                </article>
              ))}
              {products.length === 0 && (
                <p className="text-sm text-[var(--admin-muted)]">
                  No hay productos publicados. Crea el primero para verlo listado aquí.
                </p>
              )}
            </div>
          </section>
          )}

          {activeTab === 'addons' && (
          <section className="rounded-3xl border border-[var(--admin-border)] bg-white p-6 shadow-sm">
            <header className="flex flex-col gap-2 pb-4 border-b border-[var(--admin-border)] mb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
                  Catálogo de add-ons
                </p>
                <h2 className="text-xl font-semibold text-[var(--admin-text)]">
                  Crea accesorios por categoría
                </h2>
              </div>
              {addonCategoriesLoading && <span className="text-xs text-[var(--admin-muted)]">Cargando add-ons...</span>}
            </header>
            <form onSubmit={handleAddonCategorySubmit} className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Nombre de la categoría
                  <input
                    type="text"
                    value={addonCategoryForm.name}
                    onChange={(e) => setAddonCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm font-semibold text-[var(--admin-muted)]">
                  Descripción
                  <input
                    type="text"
                    value={addonCategoryForm.description}
                    onChange={(e) => setAddonCategoryForm((prev) => ({ ...prev, description: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <div className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-[var(--admin-text)]">Items de la categoría</p>
                  <button
                    type="button"
                    onClick={addAddonCategoryItem}
                    className="text-sm font-semibold text-[var(--admin-primary)]"
                  >
                    + Agregar item
                  </button>
                </div>
                {(addonCategoryForm.items || []).map((item, index) => (
                  <div
                    key={item.id || index}
                    className="grid gap-3 md:grid-cols-[2fr,1fr,auto] items-center"
                  >
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleAddonCategoryItemChange(index, 'name', e.target.value)}
                      className="rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                      placeholder="Nombre"
                    />
                    <input
                      type="number"
                      value={item.price}
                      onChange={(e) => handleAddonCategoryItemChange(index, 'price', e.target.value)}
                      className="rounded-2xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                      placeholder="Precio"
                      step="0.01"
                    />
                    <button
                      type="button"
                      onClick={() => removeAddonCategoryItem(index)}
                      className="text-sm font-semibold text-[var(--admin-danger)]"
                    >
                      Quitar
                    </button>
                  </div>
                ))}
                {(addonCategoryForm.items || []).length === 0 && (
                  <p className="text-sm text-[var(--admin-muted)]">No hay items agregados.</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="rounded-2xl bg-[var(--admin-primary)] px-5 py-2 text-sm font-semibold text-white shadow hover:bg-[var(--admin-primary-dark)]"
                >
                  {editingAddonCategoryId ? 'Actualizar categoría' : 'Crear categoría'}
                </button>
                {editingAddonCategoryId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAddonCategoryId(null);
                      setAddonCategoryForm(emptyAddonCategoryForm());
                    }}
                    className="rounded-2xl border border-[var(--admin-border)] px-4 py-2 text-sm font-semibold text-[var(--admin-muted)] hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>

            <div className="mt-6 grid gap-4">
              {addonCategories.map((category) => (
                <article
                  key={category.id}
                  className="rounded-2xl border border-[var(--admin-border)] bg-[var(--admin-surface)] p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[var(--admin-text)]">{category.name}</h3>
                      <p className="text-sm text-[var(--admin-muted)]">{category.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleAddonCategoryEdit(category)}
                        className="rounded-full border border-[var(--admin-border)] px-4 py-1 text-sm font-medium text-[var(--admin-text)]"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddonCategoryDelete(category.id)}
                        className="rounded-full bg-red-50 px-4 py-1 text-sm font-medium text-[var(--admin-danger)]"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  {category.items?.length > 0 && (
                    <div className="mt-3 text-sm text-[var(--admin-muted)]">
                      <span className="font-semibold text-[var(--admin-text)]">Items:</span>{' '}
                      {category.items
                        .map((item) => `${item.name}${Number.isFinite(item.price) ? ` (${formatCurrency(item.price)})` : ''}`)
                        .join(', ')}
                    </div>
                  )}
                </article>
              ))}
              {addonCategories.length === 0 && !addonCategoriesLoading && (
                <p className="text-sm text-[var(--admin-muted)]">
                  Aún no hay categorías de add-ons creadas.
                </p>
              )}
            </div>
          </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default EcommerceAdmin;
